/* The incident queue, and the two transitions a human can make.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere. Hard rule 1: the edge sets it from a validated token
 * claim, and a frontend that sends it is asking the backend to trust the browser.
 *
 * There is no delete mutation, and there is no re-open. Neither exists on the server either — an
 * incident is the record that something was wrong, and a recurrence is a new incident with its own
 * onset. A UI offering either would be offering something the API refuses.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { isIncident, isNotificationAttempt } from '@/shared/api/generated/observability';
import type {
  Incident,
  NotificationAttempt,
  Severity,
} from '@/shared/api/generated/observability';
import { getOne, getPage, post } from '@/shared/api/http';

import { orderForQueue, stateParamFor, usesQueueRoute } from '../model/incidents';
import type { IncidentFilter } from '../model/incidents';

export const INCIDENT_QUEUE_URL = '/v1/observability/incidents';

export function acknowledgeUrl(incidentId: string): string {
  return `${INCIDENT_QUEUE_URL}/${encodeURIComponent(incidentId)}/acknowledge`;
}

export function resolveUrl(incidentId: string): string {
  return `${INCIDENT_QUEUE_URL}/${encodeURIComponent(incidentId)}/resolve`;
}

const QUERY_KEY = ['observability', 'incidents'] as const;

/* RFC 9457 Problem Details. Every backend error serialises to this, so the UI shows the server's own
 * explanation — "cannot move an incident from acknowledged to acknowledged" is far more useful than
 * "Request failed", and on this screen a 409 usually means a colleague got there first. */

async function fetchQueue(): Promise<readonly Incident[]> {
  /* `getPage` validates per item with the generated guard, not a cast. A cast would let a malformed
   * record through with `severity: undefined`, and the tone lookup would render an unstyled badge — a
   * critical incident displayed as nothing in particular. Refusing the whole response is the loud
   * failure; showing eleven of twelve incidents would be the quiet one. */
  const page = await getPage(INCIDENT_QUEUE_URL, isIncident, 'incident');
  return orderForQueue([...page.items]);
}

/** `enabled: false` means "do not ask", and it is a permission answer rather than a lazy-load.
 *
 * **A role that cannot read this queue is not hypothetical.** `STEWARD`'s reads were hand-listed
 * while `READER`'s are derived, so for most of this platform's life the working role could not read
 * `observability.incident` at all — and a customer's hand-written role can withhold it whatever the
 * starter roles do.
 *
 * A caller that knows it lacks the grant must not issue the request: a 403 arrives as a *failed
 * read*, and a surface aggregating several sources then has to report an ordinary boundary as "we
 * could not find out", which is the sentence that has to stay reserved for a genuine fault.
 *
 * Optional and defaulted, so the queue screen and the dashboard are untouched. */
export interface IncidentQueryOptions {
  readonly enabled?: boolean;
}

export function useIncidentQueue(
  options: IncidentQueryOptions = {},
): UseQueryResult<readonly Incident[]> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchQueue,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/* One key per incident *and per transition*, minted when the row renders rather than on click.
 *
 * Minting inside the handler would produce a fresh key per attempt, so a double-click through a slow
 * network sends two requests the server treats as unrelated. Keying per (incident, action) means a
 * retry of the same decision carries the same key — and acknowledging then resolving are two
 * decisions, so they must not share one. */
const keys = new Map<string, string>();

export function idempotencyKeyFor(
  incidentId: string,
  action: 'acknowledge' | 'resolve',
): string {
  const slot = `${action}:${incidentId}`;
  const existing = keys.get(slot);
  if (existing !== undefined) return existing;
  const minted = `${action}-${incidentId}-${crypto.randomUUID()}`;
  keys.set(slot, minted);
  return minted;
}

async function transition(
  incidentId: string,
  action: 'acknowledge' | 'resolve',
): Promise<Incident> {
  const url =
    action === 'acknowledge' ? acknowledgeUrl(incidentId) : resolveUrl(incidentId);
  return post(url, idempotencyKeyFor(incidentId, action), isIncident, 'incident');
}

export function useAcknowledgeIncident(): UseMutationResult<Incident, Error, string> {
  return useTransition('acknowledge');
}

export function useResolveIncident(): UseMutationResult<Incident, Error, string> {
  return useTransition('resolve');
}

function useTransition(
  action: 'acknowledge' | 'resolve',
): UseMutationResult<Incident, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) => transition(incidentId, action),
    /* Refetch rather than patch the cache optimistically. A transition can be refused server-side —
     * another operator acknowledging first is a 409 — so the authoritative answer is the one the
     * server just wrote, and an optimistic patch would show a state that never happened. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/* ---------------------------------------------------------------- the filtered read

 * **Two routes, because the platform has two and they answer different questions.**
 *
 * `/incidents` is the queue: active only, most severe first, capped at 200, and unable to enumerate.
 * `/incidents/all` is keyset-paged, filterable on state and kind, newest first. The screen's `active`
 * filter reads the first and the other four read the second — `state` on the paged route is a single
 * equality, so "open or acknowledged" there would be two requests whose truncation is two separate
 * caps rather than one.
 *
 * The consequence is stated rather than hidden: on the `active` filter the **kind** narrowing is
 * applied in the browser, because the queue route takes no `kind`. So a tenant with more than 200
 * active incidents narrowing by kind is narrowing the 200 most severe, and `INCIDENT_QUEUE_CAP` is
 * exported so the screen can say so. Filter-then-truncate and truncate-then-filter are different
 * answers, and a screen that cannot tell which it is showing is the defect this platform records at
 * a dozen sites.
 */
export const INCIDENT_QUEUE_CAP = 200;

export const ALL_INCIDENTS_URL = `${INCIDENT_QUEUE_URL}/all`;

export interface IncidentPage {
  readonly items: readonly Incident[];
  /** Null on the last page. Non-null means there is more *and* where to resume. */
  readonly nextCursor: string | null;
  /** True when the `active` queue came back at its cap, so this view may not be the whole set. */
  readonly atQueueCap: boolean;
}

/** The screen's three narrowings plus where to resume. Severity is the design's second select; the
 * paged route takes it as a parameter and the queue route is narrowed here, like `kind`. */
export interface IncidentQuery {
  readonly filter: IncidentFilter;
  readonly kind: string | null;
  readonly severity: Severity | null;
  /** The keyset cursor for the paged route; ignored by the queue route, which does not page. */
  readonly cursor: string | null;
}

function pageUrl(query: IncidentQuery): string {
  if (usesQueueRoute(query.filter)) return INCIDENT_QUEUE_URL;
  const params = new URLSearchParams();
  const state = stateParamFor(query.filter);
  if (state !== null) params.set('state', state);
  if (query.kind !== null) params.set('kind', query.kind);
  if (query.severity !== null) params.set('severity', query.severity);
  if (query.cursor !== null) params.set('after', query.cursor);
  const search = params.toString();
  return search === '' ? ALL_INCIDENTS_URL : `${ALL_INCIDENTS_URL}?${search}`;
}

async function fetchFiltered(query: IncidentQuery): Promise<IncidentPage> {
  const page = await getPage(pageUrl(query), isIncident, 'incident');
  /* Re-ordered here whatever the route said, because the two disagree — the queue is severity-first
   * and the paged route is newest-first — and a listing whose order changes when somebody presses a
   * filter chip is a listing that appears to shuffle itself. `groupForQueue` re-derives the design's
   * order (worst first, then oldest) from the items in hand. */
  const ordered = orderForQueue([...page.items]);
  const onQueue = usesQueueRoute(query.filter);
  const narrowed = onQueue
    ? ordered.filter(
        (incident) =>
          (query.kind === null || incident.kind === query.kind) &&
          (query.severity === null || incident.severity === query.severity),
      )
    : ordered;
  return {
    items: narrowed,
    nextCursor: page.nextCursor,
    atQueueCap: onQueue && page.items.length >= INCIDENT_QUEUE_CAP,
  };
}

/** The queue under the screen's filters and page. Separate cache key per filter, kind, severity and
 * cursor, so pressing a chip back and forth does not re-fetch, and so a stale answer for one filter
 * can never be rendered under another's heading. */
export function useIncidents(query: IncidentQuery): UseQueryResult<IncidentPage> {
  return useQuery({
    queryKey: [
      ...QUERY_KEY,
      'filtered',
      query.filter,
      query.kind ?? 'every',
      query.severity ?? 'any',
      query.cursor ?? 'first',
    ],
    queryFn: () => fetchFiltered(query),
    retry: false,
  });
}

/** One incident, for its own page.
 *
 * Read on its own rather than found in the queue's cache: the detail page is a bookmarkable URL an
 * incident review arrives at directly, and a resolved incident is not in the active queue at all —
 * so a cache lookup would 404 exactly the incidents somebody is auditing. A 404 here is the server
 * saying it does not exist *for this tenant*, which is deliberately indistinguishable from another
 * tenant's id.
 */
export function useIncident(incidentId: string): UseQueryResult<Incident> {
  return useQuery({
    queryKey: [...QUERY_KEY, 'one', incidentId],
    queryFn: () =>
      getOne(
        `${INCIDENT_QUEUE_URL}/${encodeURIComponent(incidentId)}`,
        isIncident,
        'incident',
      ),
    retry: false,
  });
}

/** The delivery attempts for one incident. **The read that answers "was anybody told".**
 *
 * `getPage` with the *item* guard, matching every other envelope on this client: the helper reads
 * `items` and the meta off the body itself, so there is no generated envelope type to keep in step.
 *
 * **An empty list means no delivery was attempted, never that delivery failed**, and the panel says
 * so in words. The platform default routes critical only, so a `warning` incident on a tenant who
 * has declared no rules is recorded and deliberately not sent — and no attempt is logged, because a
 * row per non-notified incident would bury the failed ones the log exists to surface.
 *
 * `retry: false` like its siblings: a 403 is a permission answer and a 503 means this deployment
 * does not serve the read, and retrying either produces the same answer more slowly.
 */
export function useIncidentNotifications(
  incidentId: string,
): UseQueryResult<readonly NotificationAttempt[]> {
  return useQuery({
    queryKey: [...QUERY_KEY, 'notifications', incidentId],
    queryFn: async () => {
      const page = await getPage(
        `${INCIDENT_QUEUE_URL}/${encodeURIComponent(incidentId)}/notifications`,
        isNotificationAttempt,
        'notification attempt',
      );
      return page.items;
    },
    retry: false,
  });
}
